import * as React from "react";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import { Checkbox } from "../../checkbox";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import { SectionHeading } from "../section-heading";
import { apiCall } from "../../api/client";
import { assistant as assistantContract, type ProposalDto, type ChatMessageDto } from "../../api/contracts/database/assistant";
import { sql as sqlContract, type StatementResultDto } from "../../api/contracts/database/sql";
import { WriteModeToggle } from "./write-mode-toggle";

interface ThreadEntry {
  role: "user" | "assistant";
  content: string;
  proposals?: ProposalDto[];
}

function isDestructive(sqlText: string): boolean {
  return (
    /\bDROP\s+(TABLE|COLUMN)\b/i.test(sqlText) ||
    (/^\s*DELETE\b/i.test(sqlText) && !/\bWHERE\b/i.test(sqlText))
  );
}

/**
 * Chat with the linked local `claude` model about the database. The model is
 * given the live schema + protected list + current mode and PROPOSES SQL; it
 * never executes. Read proposals get a one-click Run; writes/DDL need write
 * mode (and a typed confirm when destructive) before they run through the same
 * gated runner (sql.run → runSql, source='assistant'). PRD §5.6, §11, §13.
 */
export function DatabaseAssistant({
  mode = "page",
  table,
}: {
  mode?: "page" | "panel";
  table?: string;
}) {
  const [thread, setThread] = React.useState<ThreadEntry[]>([]);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [writeMode, setWriteMode] = React.useState(false);
  const [includeSamples, setIncludeSamples] = React.useState(false);
  const [unavailable, setUnavailable] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    setError(null);
    const nextThread: ThreadEntry[] = [...thread, { role: "user", content: text }];
    setThread(nextThread);
    setInput("");
    setLoading(true);
    try {
      const messages: ChatMessageDto[] = nextThread.map((e) => ({
        role: e.role,
        content: e.content,
      }));
      const res = await apiCall(assistantContract.chat, {
        body: { messages, includeSampleRows: includeSamples, table, readOnly: !writeMode },
      });
      if (!res.modelAvailable) {
        setUnavailable(true);
        return;
      }
      setThread((t) => [
        ...t,
        { role: "assistant", content: res.explanation, proposals: res.statements },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "request failed");
    } finally {
      setLoading(false);
    }
  }

  if (unavailable) {
    return (
      <Card className="p-6">
        <CardContent className="flex flex-col items-start gap-2 p-0">
          <Icon name="sparkles" className="text-muted-foreground" />
          <SectionHeading
            as="h3"
            title="Link a model"
            description={
              <span className="block max-w-md">
                The local <code className="type-subtext">claude</code> CLI isn’t
                installed or authenticated. Install Claude Code and sign in,
                then reload — the rest of the explorer works without it.
              </span>
            }
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", mode === "panel" && "h-full")}>
      <WriteModeToggle writeMode={writeMode} onChange={setWriteMode} showDriftBanner={mode === "page"} />

      <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
        {thread.length === 0 ? (
          <p className="type-body text-muted-foreground">
            Ask about your data — e.g. “show me the five most recently updated
            tasks” or “which leads have no contacts?”. I’ll propose SQL; you
            review and run it.
          </p>
        ) : (
          thread.map((entry, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div
                className={cn(
                  "rounded-lg px-3 py-2 type-body",
                  entry.role === "user"
                    ? "self-end bg-secondary text-foreground"
                    : "text-foreground",
                )}
              >
                <span className="whitespace-pre-wrap">{entry.content}</span>
              </div>
              {entry.proposals?.map((p, pi) => (
                <ProposalCard key={pi} proposal={p} writeMode={writeMode} />
              ))}
            </div>
          ))
        )}
        {loading ? (
          <p className="type-body text-muted-foreground">Thinking…</p>
        ) : null}
        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </div>

      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 type-subtext text-muted-foreground">
          <Checkbox
            checked={includeSamples}
            onCheckedChange={(c) => setIncludeSamples(c === true)}
          />
          Include sample rows{table ? ` of ${table}` : ""} as context
        </label>
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about your data…"
            rows={mode === "panel" ? 2 : 3}
            className="flex-1 resize-y"
          />
          <Button onClick={send} disabled={loading || !input.trim()}>
            <Icon name="send" size="sm" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProposalCard({
  proposal,
  writeMode,
}: {
  proposal: ProposalDto;
  writeMode: boolean;
}) {
  const [result, setResult] = React.useState<StatementResultDto | null>(null);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState("");
  const [showConfirm, setShowConfirm] = React.useState(false);

  const isRead = proposal.kind === "read";
  const destructive = isDestructive(proposal.sql);

  async function run(confirmDestructive: boolean) {
    setRunning(true);
    setError(null);
    try {
      const res = await apiCall(sqlContract.run, {
        body: {
          sql: proposal.sql,
          readOnly: isRead,
          confirmDestructive,
        },
      });
      const stmt = res.statements[0] ?? null;
      setResult(stmt);
      if (stmt?.error) setError(stmt.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : "run failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card className="border border-border">
      <CardContent className="flex flex-col gap-2 py-3">
        {proposal.rationale ? (
          <p className="type-subtext text-muted-foreground">{proposal.rationale}</p>
        ) : null}
        <div className="flex items-start gap-2">
          <Badge variant={isRead ? "secondary" : "outline"}>{proposal.kind}</Badge>
          <pre className="flex-1 overflow-x-auto rounded-md bg-surface-3 p-2 font-mono type-subtext text-foreground">
            {proposal.sql}
          </pre>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isRead ? (
            <Button size="sm" onClick={() => run(false)} disabled={running}>
              <Icon name="play" size="sm" /> {running ? "Running…" : "Run"}
            </Button>
          ) : !writeMode ? (
            <span className="type-subtext text-muted-foreground">
              Enable write mode to run this {proposal.kind} statement.
            </span>
          ) : destructive ? (
            !showConfirm ? (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowConfirm(true)}
                disabled={running}
              >
                <Icon name="triangle-alert" size="sm" /> Review & run
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder='Type "CONFIRM"'
                  className="h-8 w-40 type-subtext"
                />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => run(true)}
                  disabled={running || confirm !== "CONFIRM"}
                >
                  Confirm & run
                </Button>
              </div>
            )
          ) : (
            <Button size="sm" onClick={() => run(false)} disabled={running}>
              <Icon name="play" size="sm" /> {running ? "Running…" : "Review & run"}
            </Button>
          )}
        </div>

        {error ? <p className="type-subtext text-destructive">{error}</p> : null}
        {result && !result.error ? <ProposalResult stmt={result} /> : null}
      </CardContent>
    </Card>
  );
}

function ProposalResult({ stmt }: { stmt: StatementResultDto }) {
  if (stmt.kind !== "read") {
    return (
      <p className="type-subtext text-muted-foreground">
        {stmt.changes ?? 0} row{(stmt.changes ?? 0) === 1 ? "" : "s"} affected.
      </p>
    );
  }
  const columns = stmt.columns ?? [];
  const rows = stmt.rows ?? [];
  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      {/* table-ignore: raw SQL-result inspector — columns are the query's
          dynamic schema (unknown at build time) and exact column identifiers +
          dense font-mono cells are intentional, so the typed <DataTable> column
          model doesn't fit. */}
      <table className="w-full type-subtext">
        <thead className="bg-surface-3">
          <tr>
            {columns.map((c) => (
              <th key={c} className="border-r border-border/40 p-1.5 text-left text-muted-foreground last:border-r-0">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, ri) => (
            <tr key={ri} className="border-t border-border/40">
              {r.map((v, ci) => (
                <td key={ci} className="border-r border-border/40 p-1.5 font-mono last:border-r-0">
                  {v === null || v === undefined ? (
                    <span className="text-muted-foreground/50 italic">NULL</span>
                  ) : (
                    String(v)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
